import React from 'react';
import PropTypes from 'prop-types';
import { Box, Center, Column, Typography } from '@tempmee/react';
import { ContentPane } from '../ContentPane';
import { FiltersCard } from '../FiltersCard';
import { ListPageHeader } from '../ListPageHeader';
import { Spinner } from '../Spinner';

/**
 * The standard count readout for a list page header: bold label, regular
 * formatted number ("**Total** 43,687"). Exported for pages that show more
 * than one count and therefore build their own `meta`.
 */
export const ListPageTotal = ({ label, value }) => (
  <Typography variant="body1">
    <Typography as="span" variant="body1" bold>
      {label}
    </Typography>{' '}
    {value !== undefined && value !== null ? new Intl.NumberFormat().format(value) : ''}
  </Typography>
);

ListPageTotal.propTypes = {
  label: PropTypes.node,
  value: PropTypes.number,
};

ListPageTotal.defaultProps = { label: 'Total', value: undefined };

/**
 * The whole Admin Panel v3 list page in one component: grey canvas, header
 * (title left; count + actions right), an optional white filter card, and the
 * page content below.
 *
 * This exists so a page migration is one wrapper instead of hand-assembling
 * ContentPane + ListPageHeader + FiltersCard — assembling them separately on
 * each page is how the pre-v3 headers drifted apart in the first place.
 *
 * `filters` takes the fields themselves (the card and its grid live here);
 * `total` covers the common single-count case, `meta` overrides it for pages
 * with several counts (compose those from ListPageTotal).
 */
export const ListPage = ({
  title,
  isLoading,
  isInitialLoading,
  total,
  meta,
  actions,
  filters,
  children,
}) => (
  <ContentPane>
    <ListPageHeader
      title={title}
      isLoading={isLoading}
      meta={meta ?? (total !== undefined ? <ListPageTotal value={total} /> : null)}
      actions={actions}
    />

    <Column gap={5}>
      {/* The two loading states are deliberately different and both live here.
          `isLoading` is a refresh of a list already on screen — ListPageHeader
          puts a small spinner beside the title and the table stays put.
          `isInitialLoading` is the reference data the filters are built from:
          there is nothing meaningful to show yet, so the filters and content
          are replaced by one centred spinner rather than rendering empty
          selects. Pages used to make this call themselves, each with its own
          wrapper. */}
      {isInitialLoading ? (
        <Center py={12}>
          <Spinner />
        </Center>
      ) : null}

      {!isInitialLoading && filters ? <FiltersCard>{filters}</FiltersCard> : null}

      {/* Wide tables scroll in their own box rather than dragging the page
          sideways — the data tables here run past 1800px and none of their
          own wrappers scroll. Only the content is wrapped: the filter card
          stays outside so its dropdowns are never clipped by this container
          (an auto overflow on one axis forces the other to auto too). */}
      {!isInitialLoading ? <Box className="min-w-0 overflow-x-auto">{children}</Box> : null}
    </Column>
  </ContentPane>
);

ListPage.propTypes = {
  title: PropTypes.node.isRequired,
  /** Shows a small spinner beside the title while the list refreshes. */
  isLoading: PropTypes.bool,
  /** Replaces the filters and content with one spinner while reference data loads. */
  isInitialLoading: PropTypes.bool,
  /** Single count shown as "**Total** n". Ignored when `meta` is given. */
  total: PropTypes.number,
  /** Custom counts cluster; overrides `total`. */
  meta: PropTypes.node,
  /** Trailing edge of the title row — a create button, a reload control. */
  actions: PropTypes.node,
  /** Filter fields; when given they render inside a FiltersCard. */
  filters: PropTypes.node,
  /** The page content — usually the table. */
  children: PropTypes.node,
};

ListPage.defaultProps = {
  isLoading: false,
  isInitialLoading: false,
  total: undefined,
  meta: null,
  actions: null,
  filters: null,
  children: null,
};

export default ListPage;
